import LegalLayout, {
  LegalH2,
  LegalP,
  LegalList,
} from "@/components/LegalLayout";

const CONTACT_EMAIL = "hola@matronaia.com";

export default function PrivacyPolicy() {
  return (
    <div data-testid="privacy-page">
      <LegalLayout
        eyebrow="Documento legal"
        title="Política de Privacidad"
        lastUpdated="1 de julio de 2026"
      >
        <LegalP>
          Matrona IA es una aplicación digital de acompañamiento durante el
          embarazo, parto, posparto, lactancia, crianza temprana, duelo
          perinatal y salud maternoinfantil. Esta política explica cómo
          tratamos la información que las usuarias deciden compartir con
          nosotras.
        </LegalP>

        <LegalH2>Responsable del tratamiento</LegalH2>
        <LegalP>
          Responsable: Matrona IA.
          <br />
          Correo electrónico de contacto:{" "}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="text-[#b7677a] underline underline-offset-4 decoration-[#f1c8d0]"
          >
            {CONTACT_EMAIL}
          </a>
        </LegalP>

        <LegalH2>Datos que podemos recopilar</LegalH2>
        <LegalP>Podemos recopilar los siguientes datos:</LegalP>
        <LegalList
          items={[
            "Nombre o alias de usuario.",
            "Correo electrónico.",
            "Datos de cuenta y acceso.",
            "Información relacionada con embarazo, parto, posparto, lactancia, crianza, duelo perinatal, salud materna o salud del recién nacido que la usuaria introduzca voluntariamente.",
            "Conversaciones mantenidas dentro de la aplicación, si se conservan para prestar el servicio.",
            "Fotografías o imágenes subidas voluntariamente por la usuaria.",
            "Información técnica básica, como dispositivo, sistema operativo, errores, registros técnicos o uso general de la aplicación.",
            "Información sobre suscripciones o compras gestionadas por Apple App Store, Google Play, RevenueCat u otros proveedores equivalentes.",
          ]}
        />

        <LegalH2>Datos de salud y datos especialmente sensibles</LegalH2>
        <LegalP>
          Matrona IA puede tratar información relacionada con embarazo, parto,
          posparto, lactancia, crianza, salud materna, salud del recién nacido y
          fotografías o imágenes subidas voluntariamente por la usuaria.
        </LegalP>
        <LegalP>
          Algunos de estos datos pueden considerarse datos de salud o datos
          especialmente sensibles. El tratamiento se realiza únicamente para
          prestar el servicio solicitado por la usuaria: ofrecer orientación
          informativa, educativa y de acompañamiento.
        </LegalP>
        <LegalP>
          Matrona IA no realiza diagnósticos médicos, no prescribe tratamientos,
          no sustituye la atención de una matrona, médico, pediatra u otro
          profesional sanitario y no debe utilizarse en situaciones de urgencia.
        </LegalP>

        <LegalH2>Base legal del tratamiento</LegalH2>
        <LegalP>
          Tratamos los datos personales de las usuarias sobre las siguientes
          bases legales:
        </LegalP>
        <LegalList
          items={[
            "Consentimiento de la usuaria: cuando introduce voluntariamente información personal, datos relacionados con salud o imágenes en la aplicación.",
            "Ejecución del servicio solicitado: para permitir el registro, acceso a la cuenta, funcionamiento del chat, personalización básica de la experiencia y gestión de la suscripción.",
            "Cumplimiento de obligaciones legales: cuando sea necesario cumplir requisitos fiscales, contables, regulatorios o responder ante autoridades competentes.",
            "Interés legítimo: para mantener la seguridad de la plataforma, prevenir usos abusivos, resolver incidencias técnicas y mejorar el funcionamiento general del servicio, siempre respetando los derechos de las usuarias.",
          ]}
        />
        <LegalP>
          La usuaria puede retirar su consentimiento en cualquier momento
          solicitando la eliminación de su cuenta o escribiendo a{" "}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="text-[#b7677a] underline underline-offset-4 decoration-[#f1c8d0]"
          >
            {CONTACT_EMAIL}
          </a>
          .
        </LegalP>

        <LegalH2>Finalidad del tratamiento</LegalH2>
        <LegalP>Los datos pueden tratarse para las siguientes finalidades:</LegalP>
        <LegalList
          items={[
            "Crear y mantener la cuenta de la usuaria.",
            "Permitir el funcionamiento de la aplicación y del chat.",
            "Ofrecer orientación informativa, educativa y de acompañamiento.",
            "Personalizar de forma básica la experiencia dentro de Matrona IA.",
            "Gestionar suscripciones, pagos, acceso Pro y restauración de compras.",
            "Atender solicitudes de soporte, incidencias o consultas.",
            "Mejorar la calidad, seguridad, precisión y utilidad del servicio.",
            "Cumplir obligaciones legales, fiscales, contables o regulatorias cuando corresponda.",
          ]}
        />

        <LegalH2>Tratamiento de imágenes</LegalH2>
        <LegalP>
          La usuaria puede subir imágenes de forma voluntaria para recibir
          orientación informativa. Estas imágenes pueden ser analizadas mediante
          sistemas automatizados de inteligencia artificial.
        </LegalP>
        <LegalP>
          Los resultados derivados del análisis de imágenes son exclusivamente
          orientativos y educativos. No constituyen diagnóstico médico ni
          sustituyen la valoración presencial o telemática de un profesional
          sanitario cualificado.
        </LegalP>
        <LegalP>
          La usuaria no debe subir imágenes de terceros sin autorización, ni
          imágenes que permitan identificar a menores u otras personas si no
          cuenta con legitimación para ello.
        </LegalP>

        <LegalH2>Uso de datos para mejora del servicio, estadísticas y estudios</LegalH2>
        <LegalP>
          Matrona IA podrá utilizar la información generada durante el uso de la
          aplicación para mejorar la calidad, seguridad, precisión y utilidad del
          servicio.
        </LegalP>
        <LegalP>
          Este uso puede incluir el análisis de consultas frecuentes, temas más
          habituales, etapas de la maternidad más consultadas, funcionamiento del
          chat, incidencias técnicas, necesidades de información de las usuarias
          y mejora de los contenidos ofrecidos.
        </LegalP>
        <LegalP>
          Siempre que sea posible, estos análisis se realizarán mediante datos
          agregados, anonimizados o seudonimizados, evitando la identificación
          directa de las usuarias.
        </LegalP>
        <LegalP>
          En caso de que en el futuro Matrona IA realice estudios, informes o
          análisis basados en datos de uso, estos se elaborarán de forma agregada
          y respetando la privacidad de las usuarias. Cuando sea necesario
          utilizar datos personales o datos especialmente sensibles con una
          finalidad distinta a la prestación del servicio, se solicitará el
          consentimiento correspondiente.
        </LegalP>
        <LegalP>
          Matrona IA no vende datos personales, datos de salud, conversaciones,
          imágenes ni información de las usuarias a terceros.
        </LegalP>
        <LegalP>
          Los datos no se utilizarán para publicidad personalizada basada en
          información de salud ni para tomar decisiones médicas automatizadas
          sobre la usuaria o su bebé.
        </LegalP>

        <LegalH2>Proveedores externos</LegalH2>
        <LegalP>
          Para prestar el servicio, Matrona IA puede utilizar proveedores
          tecnológicos externos. Estos proveedores solo tratarán los datos
          necesarios para prestar su función y no podrán utilizarlos para fines
          propios no autorizados.
        </LegalP>
        <LegalP>Entre los proveedores que pueden intervenir se incluyen:</LegalP>
        <LegalList
          items={[
            "Proveedores de inteligencia artificial para generar respuestas informativas.",
            "Proveedores de infraestructura, alojamiento web, base de datos y backend.",
            "RevenueCat u otros proveedores equivalentes para gestionar suscripciones y compras dentro de la app.",
            "Apple App Store y Google Play para procesar pagos, suscripciones, descargas y gestión de la aplicación.",
            "Herramientas técnicas de analítica, seguridad o monitorización, si se utilizan, con la finalidad de mejorar la estabilidad y seguridad del servicio.",
          ]}
        />
        <LegalP>
          Matrona IA no vende datos personales de las usuarias a terceros.
        </LegalP>

        <LegalH2>Conservación de datos</LegalH2>
        <LegalP>
          Los datos se conservarán únicamente durante el tiempo necesario para
          prestar el servicio, mantener la cuenta de la usuaria, cumplir
          obligaciones legales, atender solicitudes de soporte o garantizar la
          seguridad de la plataforma.
        </LegalP>
        <LegalP>Como criterio general:</LegalP>
        <LegalList
          items={[
            "Los datos de cuenta se conservarán mientras la cuenta esté activa.",
            "Las conversaciones, imágenes e información introducida por la usuaria se conservarán mientras sean necesarias para prestar el servicio o hasta que la usuaria solicite su eliminación.",
            "Tras una solicitud de eliminación de cuenta, los datos personales se eliminarán o anonimizarán en un plazo razonable, salvo aquellos que deban conservarse por obligación legal.",
            "Las copias de seguridad técnicas podrán conservar datos durante un periodo limitado por motivos de seguridad, tras el cual serán eliminadas o sobrescritas.",
          ]}
        />

        <LegalH2>Eliminación de cuenta y datos</LegalH2>
        <LegalP>
          La usuaria puede solicitar la eliminación de su cuenta y de los datos
          personales asociados en cualquier momento.
        </LegalP>
        <LegalP>
          Para hacerlo, puede utilizar la opción “Eliminar cuenta” o “Borrar
          cuenta” dentro de la aplicación, si está disponible, o escribir a{" "}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="text-[#b7677a] underline underline-offset-4 decoration-[#f1c8d0]"
          >
            {CONTACT_EMAIL}
          </a>{" "}
          indicando el email asociado a su cuenta y solicitando la eliminación.
        </LegalP>
        <LegalP>
          Una vez recibida la solicitud, Matrona IA eliminará o anonimizará los
          datos personales asociados a la cuenta, salvo aquellos que deban
          conservarse por obligación legal, seguridad, prevención de fraude o
          defensa frente a reclamaciones.
        </LegalP>

        <LegalH2>Derechos de la usuaria</LegalH2>
        <LegalP>
          La usuaria puede ejercer sus derechos de acceso, rectificación,
          supresión, oposición, limitación del tratamiento, portabilidad y
          retirada del consentimiento escribiendo a{" "}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="text-[#b7677a] underline underline-offset-4 decoration-[#f1c8d0]"
          >
            {CONTACT_EMAIL}
          </a>
          .
        </LegalP>
        <LegalP>
          También puede presentar una reclamación ante la autoridad de protección
          de datos competente si considera que sus derechos no han sido atendidos
          correctamente.
        </LegalP>

        <LegalH2>Decisiones automatizadas</LegalH2>
        <LegalP>
          Matrona IA utiliza sistemas automatizados de inteligencia artificial
          para generar respuestas informativas. Estas respuestas no constituyen
          decisiones médicas automatizadas, diagnósticos, prescripciones ni
          decisiones con efectos jurídicos sobre la usuaria.
        </LegalP>
        <LegalP>
          Las decisiones relacionadas con la salud de la usuaria o de su bebé
          deben tomarse siempre con el apoyo de profesionales sanitarios
          cualificados.
        </LegalP>

        <LegalH2>Seguridad</LegalH2>
        <LegalP>
          Matrona IA adopta medidas razonables para proteger la información
          frente a accesos no autorizados, alteración, pérdida o uso indebido.
        </LegalP>

        <LegalH2>Cambios en esta política</LegalH2>
        <LegalP>
          Podremos actualizar esta Política de Privacidad cuando sea necesario.
          Las modificaciones serán publicadas en esta página.
        </LegalP>

        <LegalH2>Contacto</LegalH2>
        <LegalP>
          Para cualquier consulta relacionada con privacidad:{" "}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="text-[#b7677a] underline underline-offset-4 decoration-[#f1c8d0]"
          >
            {CONTACT_EMAIL}
          </a>
        </LegalP>
      </LegalLayout>
    </div>
  );
}